'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import { Upload, Loader2, Check, HelpCircle, X, Youtube, Puzzle, RefreshCw } from 'lucide-react'
import { subscribe, guessType, isSubscribed } from '@/lib/subscriptions'
import { Button, cx } from '@/components/ui/kit'

/**
 * Import UNIQUE des abonnements YouTube via Google Takeout.
 *
 * Pourquoi Takeout et pas une « synchro » directe ? L'API YouTube qui liste les
 * abonnements exige le scope sensible `youtube.readonly`, que Google bloque pour
 * les applications non vérifiées → impossible en self-service. La voie propre et
 * fiable : l'utilisateur exporte ses abonnements (Google Takeout → YouTube →
 * abonnements → subscriptions.csv) et les importe ici, UNE fois. On les garde
 * ensuite en mémoire dans l'app (localStorage + serveur via le token reco).
 */

type Row = { channelId: string; title: string }

/** Découpe une ligne CSV en respectant les guillemets. */
function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQ = false
  for (let i = 0; i < line.length; i++) {
    const c = line[i]
    if (inQ) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++ } else inQ = false
      } else cur += c
    } else {
      if (c === '"') inQ = true
      else if (c === ',') { out.push(cur); cur = '' }
      else cur += c
    }
  }
  out.push(cur)
  return out
}

const CHAN_RE = /UC[A-Za-z0-9_-]{22}/

/** Extrait {channelId,title} d'un CSV Takeout (robuste multi-langues). */
function parseCsv(text: string): Row[] {
  const rows: Row[] = []
  const lines = text.split(/\r?\n/).filter(l => l.trim())
  for (let i = 0; i < lines.length; i++) {
    if (i === 0 && !CHAN_RE.test(lines[i])) continue // ligne d'en-tête
    const fields = parseCsvLine(lines[i]).map(f => f.trim())
    const idField = fields.find(f => CHAN_RE.test(f))
    if (!idField) continue
    const channelId = (idField.match(CHAN_RE) as RegExpMatchArray)[0]
    // Titre = dernier champ non vide qui n'est ni l'id ni une URL.
    const title = [...fields].reverse().find(f => f && !CHAN_RE.test(f) && !/^https?:\/\//i.test(f)) || 'Chaîne'
    rows.push({ channelId, title })
  }
  return rows
}

/** Extrait {channelId,title} d'un JSON Takeout (ancien format API). */
function parseJson(text: string): Row[] {
  const rows: Row[] = []
  try {
    const data = JSON.parse(text)
    const arr = Array.isArray(data) ? data : (data?.items || [])
    for (const it of arr) {
      const channelId =
        it?.snippet?.resourceId?.channelId || it?.resourceId?.channelId ||
        it?.channelId || it?.id || ''
      const title = it?.snippet?.title || it?.title || 'Chaîne'
      if (CHAN_RE.test(String(channelId))) rows.push({ channelId: String(channelId).match(CHAN_RE)![0], title: String(title) })
    }
  } catch {}
  return rows
}

export default function SubscriptionsImport({ className = '' }: { className?: string }) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<{ total: number; added: number } | null>(null)
  const [error, setError] = useState('')
  const [help, setHelp] = useState(false)

  const onFile = async (file: File) => {
    setBusy(true); setError(''); setResult(null)
    try {
      const text = await file.text()
      const rows = /\.json$/i.test(file.name) || text.trim().startsWith('[') || text.trim().startsWith('{')
        ? parseJson(text)
        : parseCsv(text)
      if (!rows.length) { setError("Aucun abonnement trouvé dans ce fichier. Utilise le subscriptions.csv de Google Takeout."); setBusy(false); return }
      // Dédoublonnage + comptage des nouveaux.
      const seen = new Set<string>()
      let added = 0
      for (const r of rows) {
        if (seen.has(r.channelId)) continue
        seen.add(r.channelId)
        if (!isSubscribed(r.channelId)) added++
        subscribe({ channelId: r.channelId, title: r.title, type: guessType(r.title) })
      }
      setResult({ total: seen.size, added })
    } catch {
      setError('Impossible de lire ce fichier.')
    }
    setBusy(false)
  }

  return (
    <section data-reveal className={cx('relative isolate overflow-hidden rounded-3xl border border-a2/25 bg-gradient-to-br from-adeep/70 via-spanel to-spanel p-5 sm:p-7', className)}>
      {/* Halo d'accent décoratif */}
      <span aria-hidden className="pointer-events-none absolute -top-24 -right-16 w-72 h-72 rounded-full bg-a2/25 blur-3xl -z-10" />
      <span aria-hidden className="pointer-events-none absolute -bottom-28 -left-20 w-64 h-64 rounded-full bg-a1/20 blur-3xl -z-10" />

      <div className="flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
        <div className="flex items-start gap-4 flex-1 min-w-0">
          <span className="w-14 h-14 rounded-2xl grid place-items-center text-white bg-gradient-to-br from-a2 to-a1 shadow-[0_0_24px_rgb(var(--a2)/.5)] shrink-0">
            <Youtube size={26} />
          </span>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold tracking-[0.28em] text-a4 mb-1">IMPORT</div>
            <h3 className="text-[19px] sm:text-[22px] font-extrabold tracking-tight leading-tight flex items-center gap-2">
              Importer mes abonnements YouTube
              <button onClick={() => setHelp(true)} title="Comment faire ?" aria-label="Comment faire ?" className="text-white/40 hover:text-a4 transition">
                <HelpCircle size={18} />
              </button>
            </h3>
            <p className="text-[14px] text-white/60 mt-1.5 leading-relaxed max-w-xl">
              Import <b className="text-white/85">unique</b> depuis Google Takeout (fichier <b className="text-white/85">subscriptions.csv</b>), gardé en mémoire dans l'app.
              Ou laisse l'extension navigateur / la synchro s'en charger.
            </p>
          </div>
        </div>

        <div className="flex flex-col gap-2.5 md:w-[260px] shrink-0">
          <Button
            icon={busy ? undefined : Upload}
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="w-full !py-3">
            {busy && <Loader2 size={15} className="animate-spin" />}
            Choisir le fichier
          </Button>
          <input ref={fileRef} type="file" accept=".csv,.json,text/csv,application/json" className="hidden"
            onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); e.currentTarget.value = '' }} />
          <div className="grid grid-cols-2 gap-2">
            <Link href="/app/extension" className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold text-white/85 bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] hover:border-a2/50 transition">
              <Puzzle size={14} /> Extension
            </Link>
            <Link href="/app/video/sync" className="inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-[13px] font-semibold text-white/85 bg-white/[0.07] border border-white/10 hover:bg-white/[0.12] hover:border-a2/50 transition">
              <RefreshCw size={14} /> Synchro
            </Link>
          </div>
        </div>
      </div>

      {(result || error) && (
        <div className="mt-4">
          {result && (
            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/30 px-3 py-1.5 text-sm text-emerald-400 font-semibold">
              <Check size={15} /> {result.added} nouveaux · {result.total} au total
            </span>
          )}
          {error && <span className="inline-block rounded-xl bg-rose-500/10 border border-rose-400/30 px-3 py-1.5 text-sm text-rose-300">{error}</span>}
        </div>
      )}

      {help && (
        <div onClick={() => setHelp(false)} className="fixed inset-0 z-[95] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div onClick={e => e.stopPropagation()} className="rounded-2xl bg-spanel border border-white/[0.09] shadow-[0_24px_60px_rgba(0,0,0,.6)] p-5 max-w-md w-full relative">
            <button onClick={() => setHelp(false)} title="Fermer" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/[0.07] hover:bg-white/[0.14] grid place-items-center text-white/70"><X size={16} /></button>
            <div className="text-[11px] font-semibold tracking-[0.28em] text-a4 mb-1">GOOGLE TAKEOUT</div>
            <div className="text-lg font-bold text-white mb-3">Récupérer ses abonnements</div>
            <ol className="text-sm text-white/70 space-y-2 list-decimal list-inside leading-relaxed marker:text-a4 marker:font-semibold">
              <li>Va sur <b>takeout.google.com</b> (connecté à ton compte YouTube).</li>
              <li>Clique <b>« Tout désélectionner »</b>, puis coche uniquement <b>YouTube et YouTube Music</b>.</li>
              <li>Dans « Plusieurs formats », garde <b>abonnements</b> (format CSV).</li>
              <li>Lance l'export, télécharge le .zip, ouvre <b>YouTube/abonnements/subscriptions.csv</b>.</li>
              <li>Reviens ici et choisis ce fichier. ✅</li>
            </ol>
            <p className="text-xs text-white/40 mt-3">
              L'API YouTube ne permet pas de récupérer les abonnements automatiquement (Google bloque ce scope pour les apps non vérifiées). Takeout est la voie officielle.
            </p>
          </div>
        </div>
      )}
    </section>
  )
}
